import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { toast } from "sonner";
import { ThunkConfig } from "../../types/responseType";
import accessControllerApis from "../../api/accessControllerApis";
import { StaffAccessResponse, Subscription } from "../../types/schoolAccess";
import { setLocalStorage } from "../../util/manageStorage";

interface AccessState {
  staff: StaffAccessResponse["staff"] | null;
  features: StaffAccessResponse["features"];
  academicCycles: StaffAccessResponse["academicCycles"];
  schoolBranch: StaffAccessResponse["schoolBranch"] | null;
  school: StaffAccessResponse["schoolDto"] | null;
  accessibleClassRooms: StaffAccessResponse["accessibleClassRooms"];
  allBranches: StaffAccessResponse["allBranches"];
  subjectEnabled: StaffAccessResponse["subjectEnabled"];
  subscription: Subscription | null;
  isLoading: boolean;
}

const initialState: AccessState = {
  staff: null,
  features: [],
  academicCycles: [],
  accessibleClassRooms: [],
  schoolBranch: null,
  allBranches: [],
  school: null,
  isLoading: false,
  subscription: null,
  subjectEnabled: [],
};

// ================== Thunk ==================
export const fetchStaffAccess = createAsyncThunk<
  StaffAccessResponse,
  { userId: number; branchCode?: string },
  ThunkConfig
>("access/fetchStaffAccess", async ({ userId, branchCode }, thunkAPI) => {
  try {
    const response = await accessControllerApis.getStaffAccess(
      userId,
      branchCode,
    );
    return response.data;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(
      error?.response?.data?.error?.message || "Failed to load access",
    );
  }
});

const accessSlice = createSlice({
  name: "access",
  initialState,
  reducers: {
    setBranch: (state, action) => {
      state.schoolBranch = action.payload;
    },
    clearAccess: (state) => {
      state.staff = null;
      state.features = [];
      state.academicCycles = [];
      state.accessibleClassRooms = [];
      state.schoolBranch = null;
      state.allBranches = [];
      state.school = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchStaffAccess.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchStaffAccess.fulfilled, (state, action) => {
        state.isLoading = false;

        state.staff = action.payload.staff;
        state.features = action.payload.features;
        state.academicCycles = action.payload.academicCycles;
        state.accessibleClassRooms = action.payload.accessibleClassRooms;
        state.schoolBranch = action.payload.schoolBranch;
        state.allBranches = action.payload.allBranches;
        state.school = action.payload.schoolDto;
        state.subscription = action.payload.subscription;
        state.subjectEnabled = action.payload.subjectEnabled;

        // Persist the branch code to local storage upon successful fetch
        if (action.payload.schoolBranch?.schoolBranchCode) {
          setLocalStorage(
            "selectedBranchCode",
            action.payload.schoolBranch.schoolBranchCode,
          );
        }
      })
      .addCase(fetchStaffAccess.rejected, (state, { payload }) => {
        state.isLoading = false;
        toast.error(payload as string);
      });
  },
});

export const { clearAccess, setBranch } = accessSlice.actions;
export default accessSlice.reducer;
